import { useParams } from 'react-router-dom'
import { TermsListSpace } from 'src/types/api'

type telecomStringType = '0' | '1' | '2'

const VALID_FORMS = ['rop01', 'rop02', 'rop03', 'rop04', 'rop05'] as const
type ValidForm = (typeof VALID_FORMS)[number]

/** A/B path 세그먼트: `slot1` ~ `slot10` (예: /rop01/xxx/slot1/{requestId}/0) */
const AB_SLOT_SEGMENT = /^slot(\d+)$/

export type ParamsListType = {
  form: ValidForm
  pCode: 'bill-letter_01' | 'bill-letter_02' | 'bill-letter_03' | 'mktJoin'
  slot?: string
  requestId: string
  telecom: telecomStringType
  fixedTelecom: '0' | '1' | '2'
  termsKey: keyof TermsListSpace.AllTermsType
  authKey: string
}

function isValidForm(value: string): value is ValidForm {
  return VALID_FORMS.includes(value as ValidForm)
}

function isTelecomString(value: string | undefined): value is telecomStringType {
  return value === '0' || value === '1' || value === '2'
}

function parseSlotNo(slot: string | undefined): number | null {
  if (!slot) return null

  const match = slot.match(AB_SLOT_SEGMENT)
  if (!match) return null

  const slotNo = Number(match[1])
  if (slotNo < 1 || slotNo > 10) return null

  return slotNo
}

/**
 * 5세그먼트 충돌 보정
 * - AB: /:form/:pCode/slot{N}/:requestId/:telecom
 * - fixedTelecom: /:form/:pCode/:requestId/:telecom/:fixedTelecom
 */
function normalizeRouteParams(params: ParamsListType) {
  let { slot, requestId = '', telecom, fixedTelecom } = params

  // AB 라우트로 잘못 매칭된 fixedTelecom URL
  if (slot && !AB_SLOT_SEGMENT.test(slot)) {
    fixedTelecom = isTelecomString(telecom) ? telecom : fixedTelecom
    telecom = isTelecomString(requestId) ? requestId : telecom
    requestId = slot
    slot = undefined
  }
  // fixedTelecom 라우트로 잘못 매칭된 AB URL
  else if (!slot && fixedTelecom != null && AB_SLOT_SEGMENT.test(requestId)) {
    slot = requestId
    requestId = telecom
    telecom = isTelecomString(fixedTelecom) ? fixedTelecom : telecom
    fixedTelecom = undefined as unknown as ParamsListType['fixedTelecom']
  }

  return { slot, requestId, telecom, fixedTelecom }
}

/** 탭 이동 등 내부 링크용: `slot1/{requestId}` 또는 `{requestId}` */
function buildRequestIdPath(slotNo: number | null, requestId: string) {
  return slotNo != null ? `slot${slotNo}/${requestId}` : requestId
}

export function useParamsList() {
  const params = useParams<keyof ParamsListType>()
  const raw = params as ParamsListType
  const { fixedTelecom, telecom, authKey, pCode, slot, requestId, termsKey } = {
    ...raw,
    ...normalizeRouteParams(raw),
  }

  const pathSegments = window.location.pathname.split('/')
  const foundValidForm = pathSegments.find((segment): segment is ValidForm => isValidForm(segment))
  const fallbackForm: ValidForm = foundValidForm ?? 'rop01'

  const form: ValidForm = isValidForm(params.form ?? '') ? (params.form as ValidForm) : fallbackForm
  const slotNo = parseSlotNo(slot)
  const requestIdPath = buildRequestIdPath(slotNo, requestId)

  return { pCode, requestId, requestIdPath, slotNo, telecom, fixedTelecom, form, termsKey, authKey }
}
